import React, { useState, useEffect, useRef } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import PropTypes from 'prop-types';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Box, Button, Chip, CircularProgress, LinearProgress,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  FormControlLabel, IconButton, InputAdornment, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DownloadIcon from '@mui/icons-material/Download';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import InfoIcon from '@mui/icons-material/Info';
import ReplayIcon from '@mui/icons-material/Replay';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Dropzone from 'react-dropzone';
import { getByCalled, ensure, create, complete, reopen, deleteById, getTaskAttachments, reorder as reorderTasks } from '../../../services/calledtask.service';
import { uploadFiles, getFile } from '../../../services/attachment.service';
import CustomizedSnackbars from "../../../components/material-snackbars";

const dropzoneAreaStyle = {
  width: '100%',
  marginTop: '10px',
  padding: '10px 15px',
  backgroundColor: '#e3e2e2',
  border: '2px dashed #b7b7b7',
  borderRadius: '5px',
  fontSize: '16px',
  color: '#7d7c7b',
  cursor: 'pointer',
  minHeight: '70px',
};

const dropzoneItemStyle = {
  width: 'fit-content',
  padding: '10px',
  backgroundColor: '#f4f4f4',
  border: '1px solid #CCC',
  borderRadius: '20px',
  marginBottom: '5px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const deleteItemStyle = { height: '16px', fill: '#d35555', cursor: 'pointer' };

const MAX_TASK_ATTACHMENTS = 5;

function TruncatedText({ text, sx, variant = 'body2', color, fontStyle }) {
  const ref = useRef(null);
  const [isClamped, setIsClamped] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight);
  }, [text]);

  if (!text) return null;

  const node = (
    <Typography
      ref={ref}
      variant={variant}
      color={color}
      sx={{
        fontStyle,
        display: '-webkit-box',
        WebkitLineClamp: 6,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        ...sx,
      }}
    >
      {text}
    </Typography>
  );

  return isClamped ? (
    <Tooltip title={text} placement="bottom-start" arrow>
      {node}
    </Tooltip>
  ) : node;
}

function CalledTasks({ calledId, taskReloadTrigger = 0, isClient, currentUserId, canDeleteFreely = false, calledIsFinished, onOpenFinishCalled, highlightTaskIds = [], intl }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '', description: '',
    textFieldRequired: false, attachmentRequired: false, attachmentMin: 1,
  });
  const [createLoading, setCreateLoading] = useState(false);

  const [completeOpen, setCompleteOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState(null);
  const [completionText, setCompletionText] = useState('');
  const [files, setFiles] = useState([]);
  const [completeError, setCompleteError] = useState('');
  const [completeLoading, setCompleteLoading] = useState(false);

  const [reopenOpen, setReopenOpen] = useState(false);
  const [taskToReopen, setTaskToReopen] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [allDoneOpen, setAllDoneOpen] = useState(false);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [detailsAttachments, setDetailsAttachments] = useState([]);
  const [detailsAttachmentsLoading, setDetailsAttachmentsLoading] = useState(false);

  const [snack, setSnack] = useState({ open: false, variant: 'success', message: '' });
  const [expanded, setExpanded] = useState(false);
  const [expandedTaskIds, setExpandedTaskIds] = useState([]);
  const [expandedGroupNames, setExpandedGroupNames] = useState(new Set());
  const [tooltipOpenIds, setTooltipOpenIds] = useState([]);
  const hasLoadedRef = useRef(false);
  const groupsInitializedRef = useRef(false);

  useEffect(() => {
    if (!hasLoadedRef.current) return;
    loadTasks();
  }, [taskReloadTrigger]);

  useEffect(() => {
    if (!highlightTaskIds || highlightTaskIds.length === 0) return;
    setExpanded(true);
    setExpandedTaskIds(prev => [...new Set([...prev, ...highlightTaskIds])]);
    setTooltipOpenIds([...highlightTaskIds]);
    const timer = setTimeout(() => setTooltipOpenIds([]), 4000);
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadTasks();
    }
    return () => clearTimeout(timer);
  }, [highlightTaskIds]);

  // Initialize all template groups as expanded on first task load
  useEffect(() => {
    if (tasks.length === 0 || groupsInitializedRef.current) return;
    groupsInitializedRef.current = true;
    const names = tasks.filter(t => t.tasktemplatename).map(t => t.tasktemplatename);
    setExpandedGroupNames(new Set(names));
  }, [tasks]);

  // Open groups that contain highlighted tasks whenever highlights or tasks change
  useEffect(() => {
    if (!highlightTaskIds || highlightTaskIds.length === 0 || tasks.length === 0) return;
    const groupNamesToOpen = tasks
      .filter(t => highlightTaskIds.includes(t.id) && !t.completed && t.tasktemplatename)
      .map(t => t.tasktemplatename);
    if (groupNamesToOpen.length === 0) return;
    setExpandedGroupNames(prev => {
      const next = new Set(prev);
      groupNamesToOpen.forEach(name => next.add(name));
      return next;
    });
  }, [highlightTaskIds, tasks]);

  function handleAccordionChange(_, isExpanded) {
    setExpanded(isExpanded);
    if (isExpanded && !hasLoadedRef.current) {
      hasLoadedRef.current = true;
      loadTasks();
    }
  }

  async function loadTasks() {
    setLoading(true);
    const ensureRes = await ensure(calledId);
    if (!ensureRes.success) {
      setSnack({ open: true, variant: 'warning', message: intl.formatMessage({ id: 'called.tasks.ensure.error' }) });
    }
    const res = await getByCalled(calledId);
    if (res.success) setTasks(res.data);
    setLoading(false);
  }

  async function refreshAndCheckAllDone() {
    const res = await getByCalled(calledId);
    if (res.success) {
      setTasks(res.data);
      if (res.data.length > 0 && res.data.every(t => t.completed)) {
        setAllDoneOpen(true);
      }
    }
  }

  function openCompleteModal(task) {
    if (!task.textfieldrequired && !task.attachmentrequired) {
      handleDirectComplete(task.id);
    } else {
      setTaskToComplete(task);
      setCompletionText('');
      setFiles([]);
      setCompleteError('');
      setCompleteOpen(true);
    }
  }

  function openReopenModal(task) {
    setTaskToReopen(task);
    setReopenOpen(true);
  }

  async function handleDirectComplete(id) {
    setActionLoading(id);
    const res = await complete(id, { completionText: '', completedByUserId: currentUserId, attachmentIds: [] });
    setActionLoading(null);
    if (res.success) {
      setSnack({ open: true, variant: 'success', message: intl.formatMessage({ id: 'called.tasks.complete.success' }) });
      await refreshAndCheckAllDone();
    }
  }

  async function handleConfirmComplete() {
    if (taskToComplete.textfieldrequired && !completionText.trim()) {
      setCompleteError(intl.formatMessage({ id: 'called.tasks.complete.text.required' }));
      return;
    }
    if (taskToComplete.attachmentrequired && files.length < taskToComplete.attachmentmin) {
      setCompleteError(intl.formatMessage({ id: 'called.tasks.complete.attach.required' }, { min: taskToComplete.attachmentmin }));
      return;
    }
    if (files.length > MAX_TASK_ATTACHMENTS) {
      setCompleteError(intl.formatMessage({ id: 'called.tasks.attach.max.error' }, { max: MAX_TASK_ATTACHMENTS }));
      return;
    }

    setCompleteLoading(true);

    const attachmentIds = [];
    if (files.length > 0) {
      const res = await uploadFiles(files, calledId);
      if (!res.success || !res.data) {
        setCompleteLoading(false);
        setCompleteError(intl.formatMessage({ id: 'process.error' }));
        return;
      }
      res.data.forEach(a => attachmentIds.push(a.id));
    }

    const res = await complete(taskToComplete.id, {
      completionText,
      completedByUserId: currentUserId,
      attachmentIds,
    });
    setCompleteLoading(false);
    if (!res.success) {
      setCompleteError(intl.formatMessage({ id: 'process.error' }));
      return;
    }
    setCompleteOpen(false);
    setTaskToComplete(null);
    setSnack({ open: true, variant: 'success', message: intl.formatMessage({ id: 'called.tasks.complete.success' }) });
    await refreshAndCheckAllDone();
  }

  async function handleConfirmReopen() {
    const id = taskToReopen.id;
    setReopenOpen(false);
    setTaskToReopen(null);
    setActionLoading(id);
    const res = await reopen(id);
    setActionLoading(null);
    if (res.success) {
      setTasks(prev => prev.map(t =>
        t.id === id
          ? { ...t, completed: false, completedat: null, completedbyuserid: null, completedbyusername: null, completiontext: null }
          : t
      ));
      setSnack({ open: true, variant: 'success', message: intl.formatMessage({ id: 'called.tasks.reopen.success' }) });
    }
  }

  function canDeleteTask(task) {
    return !isClient && (canDeleteFreely || (task.tasktemplatename == null && task.createduser == currentUserId));
  }

  async function handleConfirmDelete() {
    const id = taskToDelete.id;
    setDeleteOpen(false);
    setTaskToDelete(null);
    const res = await deleteById(id);
    if (res.success) {
      setTasks(prev => prev.filter(t => t.id !== id));
      setSnack({ open: true, variant: 'success', message: intl.formatMessage({ id: 'called.tasks.delete.success' }) });
    }
  }

  async function handleCreate() {
    if (!createForm.title.trim()) return;
    setCreateLoading(true);
    const res = await create({ calledId, ...createForm });
    setCreateLoading(false);
    if (res.success) {
      setCreateOpen(false);
      setCreateForm({ title: '', description: '', textFieldRequired: false, attachmentRequired: false, attachmentMin: 1 });
      setNewTaskTitle('');
      const listRes = await getByCalled(calledId);
      if (listRes.success) setTasks(listRes.data);
      setSnack({ open: true, variant: 'success', message: intl.formatMessage({ id: 'called.tasks.create.success' }) });
    }
  }

  function handleOpenCreate() {
    setCreateForm(prev => ({ ...prev, title: newTaskTitle.trim() }));
    setNewTaskTitle('');
    setCreateOpen(true);
  }

  async function handleOpenDetails(task) {
    setTaskToEdit(task);
    setDetailsAttachments([]);
    setDetailsOpen(true);
    if (task.completed) {
      setDetailsAttachmentsLoading(true);
      const res = await getTaskAttachments(task.id);
      setDetailsAttachments(res.success ? res.data : []);
      setDetailsAttachmentsLoading(false);
    }
  }

  async function handleDownloadAttachment(att) {
    const res = await getFile(att.hirerid, att.id, att.path, att.filerepositorymethod);
    if (res && res.data && res.data.success && res.data.data) {
      window.open(res.data.data, '_blank');
    } else {
      setSnack({ open: true, variant: 'error', message: intl.formatMessage({ id: 'file.not.found' }) });
    }
  }

  function handleAddFieldChange(e) {
    setNewTaskTitle(e.target.value);
  }

  function handleAddTaskKeyDown(e) {
    if (e.key === 'Enter' && newTaskTitle.trim()) {
      handleOpenCreate();
    }
  }

  async function handleDragEnd(result) {
    const { source, destination } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Reordenação de tasks dentro de um template
    if (source.droppableId.startsWith('template-') && source.droppableId === destination.droppableId) {
      const templateName = source.droppableId.slice('template-'.length);
      const zoneTasks = tasks.filter(t => t.tasktemplatename === templateName);
      const reordered = Array.from(zoneTasks);
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      const orderValues = zoneTasks.map(t => t.instructionorder);
      const updatedZone = reordered.map((t, i) => ({ ...t, instructionorder: orderValues[i] }));
      setTasks(prev => {
        const nonZone = prev.filter(t => t.tasktemplatename !== templateName);
        return [...nonZone, ...updatedZone].sort((a, b) => a.instructionorder - b.instructionorder);
      });
      await reorderTasks(reordered.map((t, i) => ({ id: t.id, order: orderValues[i] })));
      return;
    }

    // Reordenação de tasks avulsas no nível superior (pode mover acima/abaixo de templates)
    if (source.droppableId === 'TOP_LEVEL') {
      const newGroups = Array.from(taskGroups);
      const [movedGroup] = newGroups.splice(source.index, 1);

      // Posições dos templates após remoção da avulsa
      const firstTemplIdx = newGroups.findIndex(g => g.type === 'template');
      const lastTemplIdx = newGroups.reduce((last, g, i) => g.type === 'template' ? i : last, -1);

      let effectiveDest = destination.index;

      // Se o destino cai dentro da zona de templates, snapeia para o limite correto
      if (firstTemplIdx !== -1 && effectiveDest > firstTemplIdx && effectiveDest <= lastTemplIdx) {
        effectiveDest = source.index > destination.index
          ? firstTemplIdx       // arrastou para cima → antes do primeiro template
          : lastTemplIdx + 1;   // arrastou para baixo → após o último template
      }

      newGroups.splice(effectiveDest, 0, movedGroup);

      // Planifica a nova ordem de todas as tasks e redistribui os instructionorder existentes
      const flatTasks = newGroups.flatMap(g => g.type === 'standalone' ? [g.task] : g.tasks);
      const sortedOrders = [...tasks].map(t => t.instructionorder).sort((a, b) => a - b);
      const updatedAll = flatTasks.map((t, i) => ({ ...t, instructionorder: sortedOrders[i] }));
      setTasks(updatedAll);
      await reorderTasks(updatedAll.map(t => ({ id: t.id, order: t.instructionorder })));
    }
  }

  if (calledIsFinished) return null;
  if (!loading && tasks.length === 0 && isClient) return null;

  function renderTask(task, dragHandleProps = null, isDragging = false) {
    const isHighlighted = highlightTaskIds.includes(task.id) && !task.completed;
    return (
      <Accordion
        key={task.id}
        disableGutters
        expanded={expandedTaskIds.includes(task.id)}
        onChange={(_, isExp) => setExpandedTaskIds(prev => isExp ? [...prev, task.id] : prev.filter(id => id !== task.id))}
        sx={{ mb: 1, '&:before': { display: 'none' }, ...(isDragging ? { boxShadow: '0 4px 12px rgba(0,0,0,0.18)', opacity: 0.95 } : {}), ...(isHighlighted ? { borderColor: '#d32f2f', borderWidth: 2, boxShadow: '0 0 0 3px rgba(211,47,47,0.12)' } : {}) }}
        elevation={0}
        variant="outlined"
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={isHighlighted ? { bgcolor: 'rgba(211,47,47,0.06)' } : {}}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
            {!isClient && (
              <Box
                {...(dragHandleProps || {})}
                onClick={e => e.stopPropagation()}
                sx={{ display: 'flex', alignItems: 'center', color: 'text.disabled', cursor: 'grab', flexShrink: 0 }}
              >
                <DragIndicatorIcon sx={{ fontSize: 18 }} />
              </Box>
            )}
            {actionLoading === task.id
              ? <CircularProgress size={20} color="inherit" sx={{ flexShrink: 0 }} onClick={e => e.stopPropagation()} />
              : task.completed
                ? (
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, bgcolor: 'success.main', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(76,175,80,0.4)' }}>
                    <CheckIcon sx={{ fontSize: 14, color: '#fff', fontWeight: 700 }} />
                  </Box>
                )
                : (
                  <Box sx={{ width: 22, height: 22, borderRadius: '50%', flexShrink: 0, border: '2px solid', borderColor: isHighlighted ? '#d32f2f' : 'divider', bgcolor: 'background.paper' }} />
                )
            }
            <Typography sx={{ fontWeight: 600, flex: 1 }}>
              {task.title}
              {task.requiredtofinishcalled && (
                <Tooltip
                  open={tooltipOpenIds.includes(task.id) || undefined}
                  title={intl.formatMessage({ id: 'called.tasks.required.tooltip' })}
                  placement="top"
                  arrow
                >
                  <span style={{ color: '#d32f2f', marginLeft: 4, fontWeight: 700, cursor: 'default' }}>*</span>
                </Tooltip>
              )}
            </Typography>
            <Chip
              label={task.completed
                ? intl.formatMessage({ id: 'called.tasks.status.completed' })
                : intl.formatMessage({ id: 'called.tasks.status.pending' })
              }
              color={task.completed ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {task.completed ? (
            <>
              {task.completiontext && (
                <Box sx={{ borderLeft: 3, borderColor: 'success.main', pl: 1.5, py: 0.5, mb: 1, bgcolor: 'background.paper', borderRadius: '0 4px 4px 0' }}>
                  <TruncatedText text={`"${task.completiontext}"`} color="text.secondary" fontStyle="italic" />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {intl.formatMessage({ id: 'called.tasks.completed.by' }, {
                  name: task.completedbyusername || '—',
                  date: task.completedat ? new Date(task.completedat).toLocaleString() : '—',
                })}
              </Typography>
              {!isClient && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Tooltip title={<h5 style={{ margin: 0 }}>{intl.formatMessage({ id: 'called.tasks.btn.details' })}</h5>}>
                    <span>
                      <IconButton className="my-mexx-icon" onClick={() => handleOpenDetails(task)} disabled={actionLoading === task.id}>
                        <MenuBookIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title={<h5 style={{ margin: 0 }}>{intl.formatMessage({ id: 'called.tasks.btn.reopen' })}</h5>}>
                      <span>
                        <IconButton className="my-mexx-icon" onClick={() => openReopenModal(task)} disabled={actionLoading === task.id}>
                          <ReplayIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    {canDeleteTask(task) && (
                      <Tooltip title={<h5 style={{ margin: 0 }}>{intl.formatMessage({ id: 'delete' })}</h5>}>
                        <span>
                          <IconButton
                            className="my-mexx-icon"
                            onClick={() => { setTaskToDelete(task); setDeleteOpen(true); }}
                            sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
                            disabled={actionLoading === task.id}
                          >
                            <DeleteForeverIcon style={{ color: '#fff' }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              )}
            </>
          ) : (
            <>
              {task.description && (
                <TruncatedText text={task.description} color="text.secondary" sx={{ mb: 1 }} />
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {!isClient ? (
                  <Tooltip title={<h5 style={{ margin: 0 }}>{intl.formatMessage({ id: 'called.tasks.btn.details' })}</h5>}>
                    <span>
                      <IconButton className="my-mexx-icon" onClick={() => handleOpenDetails(task)} disabled={actionLoading === task.id}>
                        <MenuBookIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                ) : <span />}
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={<h5 style={{ margin: 0 }}>{intl.formatMessage({ id: 'called.tasks.btn.complete' })}</h5>}>
                    <span>
                      <IconButton className="my-mexx-icon" onClick={() => openCompleteModal(task)} disabled={actionLoading === task.id}>
                        <CheckIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {canDeleteTask(task) && (
                    <Tooltip title={<h5 style={{ margin: 0 }}>{intl.formatMessage({ id: 'delete' })}</h5>}>
                      <span>
                        <IconButton
                          className="my-mexx-icon"
                          onClick={() => { setTaskToDelete(task); setDeleteOpen(true); }}
                          sx={{ backgroundColor: 'error.main', '&:hover': { backgroundColor: 'error.dark' } }}
                          disabled={actionLoading === task.id}
                        >
                          <DeleteForeverIcon style={{ color: '#fff' }} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  }

  const taskGroups = (() => {
    const groups = [];
    const seenTemplates = new Map();
    for (const task of tasks) {
      const key = task.tasktemplatename || null;
      if (!key) {
        groups.push({ type: 'standalone', task });
      } else {
        if (!seenTemplates.has(key)) {
          const group = { type: 'template', name: key, tasks: [] };
          seenTemplates.set(key, group);
          groups.push(group);
        }
        seenTemplates.get(key).tasks.push(task);
      }
    }
    return groups;
  })();

  return (
    <Box sx={{ mb: 2, mt: -2 }}>
      <Accordion expanded={expanded} onChange={handleAccordionChange} disableGutters elevation={1} sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: '4px !important' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography sx={{ fontWeight: 600 }}>
            {intl.formatMessage({ id: 'called.tasks.section.title' })}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '0 0 12px 0 !important' }}>
          {loading ? (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} color="secondary" />
            </Box>
          ) : (
            <>
          <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="TOP_LEVEL" type="TOP_LEVEL">
            {(outerDrop) => (
              <div ref={outerDrop.innerRef} {...outerDrop.droppableProps}>
                {taskGroups.map((group, idx) => {
                  if (group.type === 'standalone') {
                    return (
                      <Draggable key={`standalone-${group.task.id}`} draggableId={`standalone-${group.task.id}`} index={idx} isDragDisabled={isClient}>
                        {(dragProv, dragSnap) => (
                          <div ref={dragProv.innerRef} {...dragProv.draggableProps}>
                            {renderTask(group.task, dragProv.dragHandleProps, dragSnap.isDragging)}
                          </div>
                        )}
                      </Draggable>
                    );
                  }
                  const total = group.tasks.length;
                  const done = group.tasks.filter(t => t.completed).length;
                  return (
                    <Draggable key={`template-${group.name}`} draggableId={`template-${group.name}`} index={idx} isDragDisabled>
                      {(outerDragProv) => (
                        <div ref={outerDragProv.innerRef} {...outerDragProv.draggableProps}>
                          <Accordion
                            expanded={expandedGroupNames.has(group.name)}
                            onChange={(_, isExp) => setExpandedGroupNames(prev => {
                              const next = new Set(prev);
                              if (isExp) next.add(group.name); else next.delete(group.name);
                              return next;
                            })}
                            disableGutters
                            sx={{ mb: 1, mt: 1, '&:before': { display: 'none' } }}
                            elevation={0}
                            variant="outlined"
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 1 }}>
                                <Typography sx={{ fontWeight: 600, flex: 1 }}>{group.name}</Typography>
                                <Tooltip title={intl.formatMessage({ id: 'called.tasks.template.progress.tip' }, { done, total })} placement="top">
                                  <Chip
                                    label={`${done}/${total}`}
                                    color={done === total ? 'success' : 'default'}
                                    size="small"
                                  />
                                </Tooltip>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: '0 !important', mt: 1, mx: 1 }}>
                              <Droppable droppableId={`template-${group.name}`} type={`TEMPLATE_${group.name}`}>
                                {(innerDrop) => (
                                  <div ref={innerDrop.innerRef} {...innerDrop.droppableProps} style={{ minHeight: 4 }}>
                                    {group.tasks.map((task, taskIdx) => (
                                      <Draggable key={task.id} draggableId={`task-${task.id}`} index={taskIdx} isDragDisabled={isClient}>
                                        {(taskDragProv, taskDragSnap) => (
                                          <div ref={taskDragProv.innerRef} {...taskDragProv.draggableProps}>
                                            {renderTask(task, taskDragProv.dragHandleProps, taskDragSnap.isDragging)}
                                          </div>
                                        )}
                                      </Draggable>
                                    ))}
                                    {innerDrop.placeholder}
                                  </div>
                                )}
                              </Droppable>
                            </AccordionDetails>
                          </Accordion>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {outerDrop.placeholder}
              </div>
            )}
          </Droppable>
          </DragDropContext>

          {!isClient && (
            <Box sx={{ mt: tasks.length > 0 ? 1 : 0 }}>
              <TextField
                fullWidth size="small" variant="outlined"
                placeholder={intl.formatMessage({ id: 'called.tasks.add.placeholder' })}
                value={newTaskTitle}
                onChange={handleAddFieldChange}
                onKeyDown={handleAddTaskKeyDown}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={handleOpenCreate} edge="end">
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
            </>
          )}
        </AccordionDetails>
      </Accordion>

      <Dialog open={createOpen} onClose={() => !createLoading && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: 'called.tasks.create.title' })}</DialogTitle>
        {createLoading && <LinearProgress />}
        <DialogContent sx={{ opacity: createLoading ? 0.5 : 1, pointerEvents: createLoading ? 'none' : 'auto' }}>
          <TextField
            label={intl.formatMessage({ id: 'called.tasks.create.title.label' })}
            fullWidth required sx={{ mt: 1 }}
            inputProps={{ maxLength: 250 }}
            value={createForm.title}
            onChange={e => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
          />
          <TextField
            label={intl.formatMessage({ id: 'called.tasks.create.desc.label' })}
            fullWidth multiline rows={3} autoFocus sx={{ mt: 2 }}
            value={createForm.description}
            onChange={e => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
          />
          <FormControlLabel
            sx={{ display: 'block', mt: 1 }}
            control={
              <Switch
                checked={createForm.textFieldRequired}
                onChange={e => setCreateForm(prev => ({ ...prev, textFieldRequired: e.target.checked }))}
              />
            }
            label={intl.formatMessage({ id: 'called.tasks.create.text.required' })}
          />
          <FormControlLabel
            sx={{ display: 'block', mt: 1 }}
            control={
              <Switch
                checked={createForm.attachmentRequired}
                onChange={e => setCreateForm(prev => ({ ...prev, attachmentRequired: e.target.checked }))}
              />
            }
            label={intl.formatMessage({ id: 'called.tasks.create.attach.required' })}
          />
          {createForm.attachmentRequired && (
            <TextField
              label={intl.formatMessage({ id: 'called.tasks.create.attach.min' })}
              type="number" fullWidth sx={{ mt: 2 }}
              inputProps={{ min: 1 }}
              value={createForm.attachmentMin}
              onChange={e => setCreateForm(prev => ({ ...prev, attachmentMin: parseInt(e.target.value, 10) || 1 }))}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button
            onClick={handleCreate} variant="contained"
            disabled={createLoading || !createForm.title.trim()}
          >
            {intl.formatMessage({ id: 'save' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={completeOpen} onClose={() => !completeLoading && setCompleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{intl.formatMessage({ id: 'called.tasks.complete.title' })}</DialogTitle>
        {completeLoading && <LinearProgress />}
        <DialogContent sx={{ opacity: completeLoading ? 0.5 : 1, pointerEvents: completeLoading ? 'none' : 'auto' }}>
          {taskToComplete && taskToComplete.textfieldrequired && (
            <TextField
              label={intl.formatMessage({ id: 'called.tasks.complete.text.label' })}
              fullWidth multiline rows={3} required sx={{ mt: 1 }}
              value={completionText}
              onChange={e => { setCompletionText(e.target.value); setCompleteError(''); }}
            />
          )}
          {taskToComplete && taskToComplete.attachmentrequired && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2">
                  {intl.formatMessage({ id: 'called.tasks.complete.attach.min' }, { min: taskToComplete.attachmentmin })}
                </Typography>
                <Typography variant="caption" color={files.length >= MAX_TASK_ATTACHMENTS ? 'error' : 'text.secondary'}>
                  {files.length}/{MAX_TASK_ATTACHMENTS}
                </Typography>
              </Box>
              <section style={{ width: '100%', marginBottom: '10px' }}>
                <div style={dropzoneAreaStyle}>
                  <Dropzone onDrop={accepted => setFiles(prev => [...prev, ...accepted])} disabled={files.length >= MAX_TASK_ATTACHMENTS}>
                    {({ getRootProps, getInputProps }) => (
                      <div {...getRootProps()} style={{ opacity: files.length >= MAX_TASK_ATTACHMENTS ? 0.4 : 1, pointerEvents: files.length >= MAX_TASK_ATTACHMENTS ? 'none' : 'auto' }}>
                        <input {...getInputProps()} />
                        <AttachFileIcon />
                        <Typography component="span" fontWeight="bold" fontSize={16} color="gray" sx={{ ml: 0.5 }}>
                          {files.length >= MAX_TASK_ATTACHMENTS
                            ? intl.formatMessage({ id: 'called.tasks.attach.max.reached' }, { max: MAX_TASK_ATTACHMENTS })
                            : intl.formatMessage({ id: 'drag.and.drop.files' })}
                        </Typography>
                      </div>
                    )}
                  </Dropzone>
                  {files.map((file, idx) => (
                    <div key={idx} style={dropzoneItemStyle}>
                      <a target="_blank" href={URL.createObjectURL(file)} rel="noreferrer">
                        {file.name}
                      </a>
                      <DeleteForeverIcon
                        style={deleteItemStyle}
                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                      />
                    </div>
                  ))}
                </div>
              </section>
            </Box>
          )}
          {completeError && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {completeError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteOpen(false)} disabled={completeLoading}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button
            onClick={handleConfirmComplete} variant="contained" color="success"
            disabled={completeLoading}
          >
            {intl.formatMessage({ id: 'called.tasks.btn.complete' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)}>
        <DialogTitle sx={{ m: 0, pr: 6 }}>
          {intl.formatMessage({ id: 'called.tasks.reopen.title' })}
          <IconButton
            onClick={() => setReopenOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText>
            {intl.formatMessage({ id: 'called.tasks.reopen.confirm.text' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReopenOpen(false)}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button onClick={handleConfirmReopen} variant="contained" color="primary">
            {intl.formatMessage({ id: 'called.tasks.btn.reopen' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle sx={{ m: 0, pr: 6 }}>
          {intl.formatMessage({ id: 'called.tasks.delete.title' })}
          <IconButton
            onClick={() => setDeleteOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText>
            {intl.formatMessage({ id: 'called.tasks.delete.text' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>{intl.formatMessage({ id: 'cancel' })}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {intl.formatMessage({ id: 'delete' })}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={allDoneOpen} onClose={() => setAllDoneOpen(false)}>
        <DialogTitle sx={{ m: 0, pr: 6 }}>
          {intl.formatMessage({ id: 'called.tasks.all.done.title' })}
          <IconButton
            onClick={() => setAllDoneOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <DialogContentText>
            {intl.formatMessage({ id: 'called.tasks.all.done.text' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllDoneOpen(false)}>{intl.formatMessage({ id: 'no' })}</Button>
          <Button
            onClick={() => { setAllDoneOpen(false); onOpenFinishCalled(); }}
            variant="contained" color="primary"
          >
            {intl.formatMessage({ id: 'yes' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Detalhes da task */}
      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, pr: 6 }}>
          {intl.formatMessage({ id: 'called.tasks.details.title' })}
          <IconButton
            onClick={() => setDetailsOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8, color: 'text.secondary' }}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 4, pt: '24px !important', pb: 3 }}>
          {taskToEdit && !taskToEdit.completed && (
            <>
              <Typography variant="body2" color="text.secondary">
                {intl.formatMessage({ id: 'called.tasks.details.pending.info' })}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {intl.formatMessage({ id: 'called.tasks.details.pending.hint' })}
              </Typography>
            </>
          )}
          {taskToEdit && taskToEdit.completed && (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                {intl.formatMessage({ id: 'called.tasks.completed.by' }, {
                  name: taskToEdit.completedbyusername || '—',
                  date: taskToEdit.completedat ? new Date(taskToEdit.completedat).toLocaleString() : '—',
                })}
              </Typography>
              {taskToEdit.completiontext ? (
                <Box sx={{ borderLeft: 3, borderColor: 'success.main', pl: 1.5, py: 0.5, mb: 2, borderRadius: '0 4px 4px 0' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    "{taskToEdit.completiontext}"
                  </Typography>
                </Box>
              ) : null}
              {detailsAttachmentsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: 'loading' })}
                  </Typography>
                </Box>
              ) : detailsAttachments.length > 0 ? (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {intl.formatMessage({ id: 'called.tasks.details.attachments' })}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {detailsAttachments.map(att => (
                      <Box key={att.id} onClick={() => handleDownloadAttachment(att)} sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}>
                        <AttachFileIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.description || att.path}
                        </Typography>
                        <DownloadIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              ) : null}
              {!taskToEdit.completiontext && detailsAttachments.length === 0 && !detailsAttachmentsLoading && (
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'called.tasks.details.no.data' })}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {intl.formatMessage({ id: 'called.tasks.details.reopen.to.edit' })}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>
            {intl.formatMessage({ id: 'cancel' })}
          </Button>
        </DialogActions>
      </Dialog>

      <CustomizedSnackbars
        variant={snack.variant}
        message={snack.message}
        isOpen={snack.open}
        toClose={() => setSnack(s => ({ ...s, open: false }))}
      />
    </Box>
  );
}

CalledTasks.propTypes = {
  calledId:           PropTypes.number.isRequired,
  taskReloadTrigger:  PropTypes.number,
  isClient:           PropTypes.bool.isRequired,
  currentUserId:      PropTypes.number.isRequired,
  calledIsFinished:   PropTypes.bool.isRequired,
  onOpenFinishCalled: PropTypes.func.isRequired,
  intl:               PropTypes.object.isRequired,
};

export default CalledTasks;
